// Uploaded pictures (slice 06, issue 746; design/23-uploaded-pictures.md §3,
// design/08-map-on-the-table.md §6, plan §2.7): what the table takes, how
// big, and the exact words of every "no". The sentences live here so the
// server (which decides) and the browser (which may say it before sending
// 25 MB) cannot disagree, and so a test can hold each sentence to its input.
export const ASSET_KINDS = ['map', 'token', 'prop'];
export const isAssetKind = (v) => typeof v === 'string' && ASSET_KINDS.includes(v);
/** Whether a format is a video (drawn from a <video>), not a picture. */
export const isVideoFormat = (f) => f === 'webm';
export const PICTURE_LIMITS = {
    /** Bytes a file may be (a token or a prop; a map has `mapBytes`). */
    bytes: 25 * 1024 * 1024,
    /**
     * Bytes a map may be (issue 1172; trillhc, 2026-10-12): real drawn maps far past 8,000 px are 10 to 40 MB as a JPEG
     * (a poster scan at 300 dpi up to about 50), so 50 MB covers them; only noise, not a drawing, reaches 200 MB at
     * 32,000 px. The server holds the upload in memory while it builds the map's pyramid, so this is also its bound.
     */
    mapBytes: 50 * 1024 * 1024,
    /** Pixels a side up to which a picture is comfortable anywhere. */
    comfortablePx: 4000,
    /** Pixels a side up to which a picture is accepted with the warning (a token or a prop; a map has `mapMaxPx`). */
    maxPx: 8000,
    /**
     * Pixels a side a map may be (issue 1172). Above `maxPx` a map is drawn from its pyramid (`isTiledMap`), so no browser
     * holds it whole; 32,000 is what one build sustains on berry (21 s, 452 MB peak, measured) and four times today's side.
     */
    mapMaxPx: 32000,
    name: 120,
    /** Bytes an animated picture or a video loop may be (issue 1176): a token plays it every frame, on every screen. */
    animatedBytes: 8 * 1024 * 1024,
    /** Milliseconds a video loop may last (issue 1176). A WebM that does not say its length is taken on its size alone. */
    videoMs: 10_000,
};
export const FORMATS_SENTENCE = 'PNG, JPEG, WebP, GIF or WebM';
const px = (n) => n.toLocaleString('en-US');
const mb = (bytes) => `${Math.round(bytes / (1024 * 1024))} MB`;
/**
 * The decision on an upload, in the plain words of design/23 §3.3. Returns
 * a refusal with its sentence, or an acceptance with the warning a large
 * picture carries.
 */
export function judgePicture(sniffed, bytes, kind) {
    const limit = byteLimit(kind);
    if (bytes > limit)
        return { ok: false, reason: `This file is ${mb(bytes)}. The limit is ${mb(limit)}.` };
    switch (sniffed.kind) {
        case 'pdf': return { ok: false, reason: `That's a PDF. The table takes pictures — ${FORMATS_SENTENCE}. Save the page as an image first.` };
        case 'mp4': return { ok: false, reason: "That's an MP4 video. The table takes a video loop as WebM — save it as WebM and try again." };
        case 'unknown': return { ok: false, reason: `That's not a picture the table can take — ${FORMATS_SENTENCE}.` };
        case 'unreadable': return { ok: false, reason: "This picture couldn't be read." };
        case 'picture': break;
    }
    const moving = sniffed.animated || isVideoFormat(sniffed.format);
    if (moving && kind === 'map')
        return { ok: false, reason: 'A map is a still picture — PNG, JPEG, WebP or a still GIF. Animated pictures and video loops are for tokens and props.' };
    if (moving && bytes > PICTURE_LIMITS.animatedBytes)
        return { ok: false, reason: `This file is ${mb(bytes)}. The limit for an animated picture or a video loop is ${mb(PICTURE_LIMITS.animatedBytes)}.` };
    if (isVideoFormat(sniffed.format) && sniffed.durationMs != null && sniffed.durationMs > PICTURE_LIMITS.videoMs) {
        return { ok: false, reason: `This video is ${(sniffed.durationMs / 1000).toFixed(1)} seconds long. The table takes video loops up to ${PICTURE_LIMITS.videoMs / 1000} seconds.` };
    }
    const what = kind === 'map' ? 'map' : 'picture';
    const side = Math.max(sniffed.width, sniffed.height);
    const most = kind === 'map' ? PICTURE_LIMITS.mapMaxPx : PICTURE_LIMITS.maxPx;
    if (side > most) {
        return { ok: false, reason: `This ${what} is ${px(sniffed.width)} × ${px(sniffed.height)}. The table takes ${what}s up to ${px(most)} on each side. Scale it down and try again.` };
    }
    if (side > PICTURE_LIMITS.comfortablePx)
        return { ok: true, warning: `Large ${what}s can be slow on older laptops.` };
    return { ok: true, warning: null };
}
/** The bytes a file of this kind may be: a map's limit is its own (issue 1172). */
export const byteLimit = (kind) => (kind === 'map' ? PICTURE_LIMITS.mapBytes : PICTURE_LIMITS.bytes);
/** Library tags (issue 1036, BOARD-018): at most ten a picture, each 1–30 characters once trimmed. */
export const TAG_LIMITS = { count: 10, length: 30 };
/** The library's orders (issue 1036). */
export const LIBRARY_SORTS = ['newest', 'oldest', 'used'];
export const isLibrarySort = (v) => typeof v === 'string' && LIBRARY_SORTS.includes(v);
/** One tag, trimmed and lowercased, or null: not a string, empty, too long, or holding a control character. */
export function parseTag(v) {
    if (typeof v !== 'string')
        return null;
    const t = v.trim().toLowerCase();
    // eslint-disable-next-line no-control-regex
    if (t.length < 1 || t.length > TAG_LIMITS.length || /[\u0000-\u001f\u007f]/.test(t))
        return null;
    return t;
}
/** A whole list of tags, repeats dropped and order kept, or null when any one is bad or there are more than ten. */
export function parseTags(v) {
    if (!Array.isArray(v) || v.length > TAG_LIMITS.count)
        return null;
    const out = [];
    for (const x of v) {
        const t = parseTag(x);
        if (t === null)
            return null;
        if (!out.includes(t))
            out.push(t);
    }
    return out;
}
/** The empty grid's extent when there is no map (plan §2.10): 30 × 30 squares at 70 px. */
export const EMPTY_GRID = { squares: 30, squarePx: 70 };
export const EMPTY_BOARD_PX = EMPTY_GRID.squares * EMPTY_GRID.squarePx;
/** The board's extent in pixels: the map at natural size, or the empty grid. */
export function boardExtent(map) {
    return map ? { width: map.width, height: map.height } : { width: EMPTY_BOARD_PX, height: EMPTY_BOARD_PX };
}
export const ZOOM_LIMITS = { min: 0.05, max: 8 };
export function parseBoardView(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const { x, y, zoom } = value;
    const finite = (v) => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= 1e7;
    if (!finite(x) || !finite(y) || !finite(zoom) || zoom < ZOOM_LIMITS.min || zoom > ZOOM_LIMITS.max)
        return null;
    return { x, y, zoom };
}
//# sourceMappingURL=pictures.js.map